import classNames from "classnames";
import React, { useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "../../../../shared/hooks/useDebouncedValue";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import { KuikaListTableUserCell } from "../../../ui-kit/kuika-list-table/kuika-list-table";
import { KuikaSkeletonTable } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import type { IVersionRow } from "./version-history";
import {
  ADD_DESCRIPTION_LABEL,
  canCompareVersions,
  exporterInitials,
  filterVersionRows,
  formatExportedAt,
  hasActivatableSelection,
  hasDeactivatableSelection,
  isSelectionEnabled,
  UNNAMED_VERSION_LABEL,
  VERSION_COMPARE_COUNT
} from "./version-history";
import "./version-history-table.scss";

/**
 * Version History table — purely presentational. Every value arrives as a prop and every
 * mutation leaves as a callback; the tab above owns fetching, persistence and the modals.
 *
 * Built as a CSS grid rather than on KuikaTable/KuikaListTable for the same reason the
 * sibling `kuika-parameters-table` is: the cells are controls, not read-only data. The
 * name and description cells swap into full-bleed editors that must cover the grid line,
 * the description editor grows its row, and the selection toolbar is an extra band inside
 * the table frame above the column header. A TanStack-backed table would need all three
 * of those bolted on through className overrides.
 */
export interface IVersionHistoryTableProps {
  rows: IVersionRow[];
  search: string;
  onSearchChange: (query: string) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onRenameVersion: (row: IVersionRow, name: string) => void;
  onDescribeVersion: (row: IVersionRow, description: string) => void;
  /** Selection toolbar's bulk "Deactivate". Receives the selection verbatim. */
  onDeactivateVersions: (ids: string[]) => void;
  /** Selection toolbar's bulk "Activate". Receives the selection verbatim. */
  onActivateVersions: (ids: string[]) => void;
  /** Selection toolbar's "Compare Versions". Only fires with exactly two ids selected. */
  onCompareVersions: (ids: string[]) => void;
  onViewDiagram: (row: IVersionRow) => void;
  onActivateVersion: (row: IVersionRow) => void;
  onDeactivateVersion: (row: IVersionRow) => void;
  isLoading?: boolean;
}

type VersionEditableField = "name" | "description";

interface IVersionCellEdit {
  rowId: string;
  field: VersionEditableField;
}

const BLOCK: string = "kuika-version-history";

const TITLE: string = "Version History";
const SUBTITLE: string =
  "Every export creates a workflow version. Preview builds are collapsed into the version they were exported as.";
const SEARCH_PLACEHOLDER: string = "Search name or description";
const DESCRIPTION_EDIT_PLACEHOLDER: string = "What changed in this version?";
/** Placeholder for rows minted before the backend carried the export instant / exporting user. */
const NO_VALUE: string = "—";

const ICON_SIZE: number = 16;
const INLINE_ICON_SIZE: number = 14;

const VersionHistoryTable = ({
  rows,
  search,
  onSearchChange,
  selectedIds,
  onSelectionChange,
  onRenameVersion,
  onDescribeVersion,
  onDeactivateVersions,
  onActivateVersions,
  onCompareVersions,
  onViewDiagram,
  onActivateVersion,
  onDeactivateVersion,
  isLoading = false
}: IVersionHistoryTableProps): JSX.Element => {
  const [edit, setEdit] = useState<IVersionCellEdit | null>(null);
  const [draft, setDraft] = useState<string>("");
  /**
   * True once the open edit has been settled — committed or cancelled. Both paths unmount the
   * editor, and removing a focused node can still surface a blur; without the latch, Enter
   * (or Cmd+Enter) would commit and the trailing blur would commit the same edit a second
   * time, and Escape's revert could be overwritten by it. Reset when the next edit opens.
   */
  const editSettledRef = useRef<boolean>(false);

  // The input echoes `search` per keystroke; the rows re-filter only once typing pauses.
  const filterQuery: string = useDebouncedValue(search);
  const visibleRows: IVersionRow[] = useMemo(
    (): IVersionRow[] => filterVersionRows(rows, filterQuery),
    [rows, filterQuery]
  );
  const selection: Set<string> = useMemo((): Set<string> => new Set<string>(selectedIds), [selectedIds]);

  const selectedCount: number = selectedIds.length;
  const visibleSelectedCount: number = visibleRows.filter((row: IVersionRow): boolean => selection.has(row.id)).length;
  const allVisibleSelected: boolean = visibleRows.length > 0 && visibleSelectedCount === visibleRows.length;
  const isIndeterminate: boolean = selectedCount > 0 && !allVisibleSelected;
  // Selection machinery hides entirely on a 0–1 row history (see isSelectionEnabled).
  const selectionEnabled: boolean = isSelectionEnabled(rows);
  const hasSelection: boolean = selectionEnabled && selectedCount > 0;

  const isEditing = (rowId: string, field: VersionEditableField): boolean =>
    edit !== null && edit.rowId === rowId && edit.field === field;

  const beginEdit = (row: IVersionRow, field: VersionEditableField): void => {
    editSettledRef.current = false;
    setDraft(field === "name" ? row.name : row.description);
    setEdit({ rowId: row.id, field });
  };

  const cancelEdit = (): void => {
    editSettledRef.current = true;
    setEdit(null);
  };

  const commitEdit = (row: IVersionRow, field: VersionEditableField): void => {
    if (editSettledRef.current) {
      return;
    }
    editSettledRef.current = true;
    setEdit(null);
    const next: string = draft.trim();
    const current: string = field === "name" ? row.name : row.description;
    // An unchanged value is not an edit — never spend a request on it.
    if (next === current) {
      return;
    }
    if (field === "name") {
      onRenameVersion(row, next);
      return;
    }
    onDescribeVersion(row, next);
  };

  const handleEditorKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    row: IVersionRow,
    field: VersionEditableField
  ): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      cancelEdit();
      return;
    }
    // Enter belongs to the textarea's own line breaks; the modifier is the commit.
    if (field === "description" && event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commitEdit(row, field);
    }
  };

  const setRowSelected = (row: IVersionRow, checked: boolean): void => {
    if (checked === selection.has(row.id)) {
      return;
    }
    if (checked) {
      onSelectionChange([...selectedIds, row.id]);
      return;
    }
    onSelectionChange(selectedIds.filter((id: string): boolean => id !== row.id));
  };

  /** The header checkbox governs the rows currently on screen; a filtered-out selection survives it. */
  const setAllVisibleSelected = (checked: boolean): void => {
    const visibleIds: Set<string> = new Set<string>(visibleRows.map((row: IVersionRow): string => row.id));
    if (!checked) {
      onSelectionChange(selectedIds.filter((id: string): boolean => !visibleIds.has(id)));
      return;
    }
    const next: string[] = [...selectedIds];
    visibleRows.forEach((row: IVersionRow): void => {
      if (!selection.has(row.id)) {
        next.push(row.id);
      }
    });
    onSelectionChange(next);
  };

  const rowLabel = (row: IVersionRow): string => `${row.name.trim() || UNNAMED_VERSION_LABEL} V${row.versionNumber}`;

  const renderSelectionToolbar = (): JSX.Element => {
    const canCompare: boolean = canCompareVersions(selectedIds);
    const compareButton: JSX.Element = (
      <KuikaButton
        type="text"
        size="sm"
        disabled={!canCompare}
        onClick={(): void => onCompareVersions(selectedIds)}
        data-testid="versionHistoryCompareVersions"
      >
        <KuikaSvg name="arrows-horizontal" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />
        Compare Versions
      </KuikaButton>
    );
    return (
      <div className={`${BLOCK}__selection`} data-testid="versionHistorySelectionToolbar">
        <div className={`${BLOCK}__selection-info`}>
          <span className={`${BLOCK}__selection-count`} aria-live="polite">
            <strong>{selectedCount}</strong> of <strong>{rows.length}</strong> selected
          </span>
          <span className={`${BLOCK}__selection-divider`} aria-hidden="true" />
          <KuikaButton
            type="text"
            size="sm"
            onClick={(): void => onSelectionChange([])}
            data-testid="versionHistoryClearSelection"
          >
            <KuikaSvg name="x" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />
            Clear Selection
          </KuikaButton>
        </div>
        <div className={`${BLOCK}__selection-actions`}>
          {canCompare ? (
            compareButton
          ) : (
            // A disabled button swallows pointer events, so the tooltip needs a live anchor
            // around it to explain WHY the action is unavailable.
            <KuikaTooltip content={`Select ${VERSION_COMPARE_COUNT} versions to compare`}>
              <span className={`${BLOCK}__selection-tooltip-anchor`}>{compareButton}</span>
            </KuikaTooltip>
          )}
          {/* Buttons are verbs, and only the verbs the selection can act on appear: a
              uniform selection gets one, a mixed one gets both. */}
          {hasActivatableSelection(rows, selectedIds) && (
            <KuikaButton
              type="text"
              size="sm"
              onClick={(): void => onActivateVersions(selectedIds)}
              data-testid="versionHistoryActivateSelection"
            >
              <KuikaSvg name="circle-check" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />
              Activate
            </KuikaButton>
          )}
          {hasDeactivatableSelection(rows, selectedIds) && (
            <KuikaButton
              type="text"
              color="danger"
              size="sm"
              onClick={(): void => onDeactivateVersions(selectedIds)}
              data-testid="versionHistoryDeactivateSelection"
            >
              <KuikaSvg name="ban" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />
              Deactivate
            </KuikaButton>
          )}
        </div>
      </div>
    );
  };

  const renderEditTrigger = (row: IVersionRow, field: VersionEditableField, label: string): JSX.Element => (
    <KuikaButton
      type="text"
      color="neutral"
      isIcon
      className={`${BLOCK}__cell-edit`}
      aria-label={label}
      onClick={(): void => beginEdit(row, field)}
      data-testid={`versionHistoryEdit-${field}-${row.id}`}
    >
      <KuikaSvg name="edit" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />
    </KuikaButton>
  );

  const renderVersionCell = (row: IVersionRow): JSX.Element => {
    const editingName: boolean = isEditing(row.id, "name");
    return (
      <div
        className={classNames(`${BLOCK}__cell`, `${BLOCK}__cell--version`, {
          [`${BLOCK}__cell--editing`]: editingName
        })}
        role="cell"
      >
        {selectionEnabled && (
          <KuikaCheckbox
            className={`${BLOCK}__row-check`}
            checked={selection.has(row.id)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setRowSelected(row, event.target.checked)}
          />
        )}
        <span className={classNames(`${BLOCK}__name-group`, { [`${BLOCK}__name-group--editing`]: editingName })}>
          {editingName ? (
            <KuikaInput
              className={`${BLOCK}__name-input`}
              size="md"
              borderless
              autoFocus
              value={draft}
              placeholder={UNNAMED_VERSION_LABEL}
              onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                setDraft(String(value))
              }
              onPressEnter={(): void => commitEdit(row, "name")}
              onBlur={(): void => commitEdit(row, "name")}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void =>
                handleEditorKeyDown(event, row, "name")
              }
              data-testid={`versionHistoryNameInput-${row.id}`}
            />
          ) : (
            <span
              className={classNames(`${BLOCK}__text`, { [`${BLOCK}__text--placeholder`]: !row.name })}
              title={row.name || undefined}
              data-testid="versionHistoryNameText"
            >
              {row.name || UNNAMED_VERSION_LABEL}
            </span>
          )}
          <span className={`${BLOCK}__version-number`} data-testid={`versionHistoryVersionNumber-${row.versionNumber}`}>
            V{row.versionNumber}
          </span>
        </span>
        {row.isLatest && <KuikaBadge content="LATEST" color="blue" />}
        {!editingName && renderEditTrigger(row, "name", `Rename ${rowLabel(row)}`)}
      </div>
    );
  };

  const renderDescriptionCell = (row: IVersionRow): JSX.Element => {
    const editingDescription: boolean = isEditing(row.id, "description");
    return (
      <div
        className={classNames(`${BLOCK}__cell`, {
          [`${BLOCK}__cell--editing`]: editingDescription,
          [`${BLOCK}__cell--flush`]: editingDescription
        })}
        role="cell"
      >
        {editingDescription ? (
          <KuikaInput
            className={`${BLOCK}__description-input`}
            type="textarea"
            size="md"
            borderless
            resizable={false}
            autoFocus
            value={draft}
            placeholder={DESCRIPTION_EDIT_PLACEHOLDER}
            onChange={(_event: React.ChangeEvent<HTMLTextAreaElement>, value: string | number): void =>
              setDraft(String(value))
            }
            onBlur={(): void => commitEdit(row, "description")}
            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>): void =>
              handleEditorKeyDown(event, row, "description")
            }
            data-testid={`versionHistoryDescriptionInput-${row.id}`}
          />
        ) : (
          <>
            <span
              className={classNames(`${BLOCK}__text`, { [`${BLOCK}__text--placeholder`]: !row.description })}
              title={row.description || undefined}
              data-testid="versionHistoryDescriptionText"
            >
              {row.description || ADD_DESCRIPTION_LABEL}
            </span>
            {renderEditTrigger(row, "description", `Describe ${rowLabel(row)}`)}
          </>
        )}
      </div>
    );
  };

  const renderActionsCell = (row: IVersionRow): JSX.Element => {
    const label: string = rowLabel(row);
    const options: KuikaDropdownMenuItemType[] = [
      {
        key: "activate",
        label: "Activate",
        icon: <KuikaSvg name="circle-check" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />,
        disabled: row.isActive,
        onClick: (): void => onActivateVersion(row)
      },
      {
        key: "deactivate",
        label: "Deactivate",
        icon: <KuikaSvg name="ban" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />,
        disabled: !row.isActive,
        onClick: (): void => onDeactivateVersion(row)
      }
    ];

    return (
      <div className={classNames(`${BLOCK}__cell`, `${BLOCK}__cell--actions`)} role="cell">
        <KuikaTooltip content="View diagram">
          <KuikaButton
            type="secondary"
            color="neutral"
            isIcon
            aria-label={`View the diagram of ${label}`}
            onClick={(): void => onViewDiagram(row)}
            data-testid={`versionHistoryViewDiagram-${row.id}`}
          >
            <KuikaSvg name="file-description" width={ICON_SIZE} height={ICON_SIZE} />
          </KuikaButton>
        </KuikaTooltip>
        <KuikaDropdownMenu placement="bottomRight" options={options}>
          <KuikaButton
            type="secondary"
            color="neutral"
            isIcon
            aria-label={`More actions for ${label}`}
            data-testid={`versionHistoryRowMenu-${row.id}`}
          >
            <KuikaSvg name="dots" width={ICON_SIZE} height={ICON_SIZE} />
          </KuikaButton>
        </KuikaDropdownMenu>
      </div>
    );
  };

  const renderRow = (row: IVersionRow): JSX.Element => {
    // Both cells degrade to their placeholder on rows minted before the backend carried
    // the export instant / exporting user (older backends, pre-auditing rows).
    const exportedAtLabel: string = formatExportedAt(row.exportedAt);
    return (
      <div
        key={row.id}
        className={classNames(`${BLOCK}__row`, { [`${BLOCK}__row--selected`]: selection.has(row.id) })}
        role="row"
        data-testid={`versionHistoryRow-${row.id}`}
      >
        {renderVersionCell(row)}
        <div className={`${BLOCK}__cell`} role="cell">
          <span className={classNames(`${BLOCK}__text`, { [`${BLOCK}__text--placeholder`]: !exportedAtLabel })}>
            {exportedAtLabel || NO_VALUE}
          </span>
        </div>
        <div className={`${BLOCK}__cell`} role="cell">
          <KuikaListTableUserCell
            caption={row.exportedByName || undefined}
            name={row.exportedByName || undefined}
            photoUrl={row.exportedByPhotoUrl || undefined}
            initials={exporterInitials(row.exportedByName) || undefined}
            fallback={NO_VALUE}
          />
        </div>
        {renderDescriptionCell(row)}
        <div className={`${BLOCK}__cell`} role="cell">
          <KuikaBadge
            content={row.isActive ? "ACTIVE" : "INACTIVE"}
            color={row.isActive ? "green" : "grey"}
            size="small"
          />
        </div>
        {renderActionsCell(row)}
      </div>
    );
  };

  const renderEmptyState = (): JSX.Element => {
    const isFiltered: boolean = filterQuery.trim().length > 0;
    const title: string = isFiltered ? "No versions match your search" : "No versions exported yet";
    const description: string = isFiltered
      ? "Try a different name or description."
      : "Exporting this process creates its first version.";
    return (
      <div className={`${BLOCK}__empty`} data-testid="versionHistoryEmptyState">
        <KuikaEmptyState variant="page" svgName="automation" customName={title} description={description} />
      </div>
    );
  };

  return (
    <div className={BLOCK} data-testid="versionHistoryTable">
      <div className={`${BLOCK}__header`}>
        <div className={`${BLOCK}__heading`}>
          <span className={`${BLOCK}__title`}>{TITLE}</span>
          <span className={`${BLOCK}__subtitle`} title={SUBTITLE}>
            {SUBTITLE}
          </span>
        </div>
        <KuikaInput
          className={`${BLOCK}__search`}
          size="sm"
          value={search}
          placeholder={SEARCH_PLACEHOLDER}
          leftIcon={<KuikaSvg name="search" width={INLINE_ICON_SIZE} height={INLINE_ICON_SIZE} />}
          onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            onSearchChange(String(value))
          }
          data-testid="versionHistorySearch"
        />
      </div>

      <div className={`${BLOCK}__frame`} aria-busy={isLoading}>
        {hasSelection && renderSelectionToolbar()}
        {/* Row-shaped skeleton instead of a spinner (the shared loading primitive): the
            first load previews the table it is fetching. A reload with rows on screen
            keeps them visible — the refresh is silent, same as the process gallery. */}
        {isLoading && rows.length === 0 ? (
          <div className={`${BLOCK}__skeleton`} data-testid="versionHistoryLoading">
            <KuikaSkeletonTable rows={3} rowHeight={40} header />
          </div>
        ) : (
          <div className={`${BLOCK}__table`} role="table" aria-label={TITLE}>
            <div className={`${BLOCK}__head`} role="rowgroup">
              <div className={`${BLOCK}__row`} role="row">
                <div className={`${BLOCK}__head-cell ${BLOCK}__head-cell--version`} role="columnheader">
                  {selectionEnabled && (
                    <KuikaCheckbox
                      className={`${BLOCK}__select-all`}
                      checked={allVisibleSelected}
                      indeterminate={isIndeterminate}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        setAllVisibleSelected(event.target.checked)
                      }
                    />
                  )}
                  Version
                </div>
                <div className={`${BLOCK}__head-cell`} role="columnheader">
                  Date &amp; Time
                </div>
                <div className={`${BLOCK}__head-cell`} role="columnheader">
                  Exported by
                </div>
                <div className={`${BLOCK}__head-cell`} role="columnheader">
                  Description
                </div>
                <div className={`${BLOCK}__head-cell`} role="columnheader">
                  Status
                </div>
                <div className={`${BLOCK}__head-cell`} role="columnheader">
                  Actions
                </div>
              </div>
            </div>
            {visibleRows.length > 0 && (
              <div className={`${BLOCK}__body`} role="rowgroup">
                {visibleRows.map(renderRow)}
              </div>
            )}
          </div>
        )}
        {visibleRows.length === 0 && !isLoading && renderEmptyState()}
      </div>
    </div>
  );
};

export default VersionHistoryTable;
